import { action, computed, observable } from 'mobx'
import type {
  TextAnswer,
  TextQuestion,
} from '@/features/jobs/questions/questions'
import { QuestionAnswerEditorViewModelBase } from '@/features/jobs/questions/QuestionAnswerEditorViewModelBase'
import { QuestionValidationResult } from '@/features/jobs/questions/question-validation-result'

/**
 * Question answer editor for `Text` questions.
 */
export class TextQuestionAnswerEditorViewModel extends QuestionAnswerEditorViewModelBase<TextQuestion> {
  /** @inheritDoc */
  readonly type = 'Text'

  /**
   * The committed text.
   */
  @observable accessor value = ''

  /**
   * The text being typed, committed by {@link flush}.
   */
  @observable accessor editingValue = ''

  /** @inheritDoc */
  @computed get isAnswered() {
    return this.value !== ''
  }

  /**
   * An empty text is still sent, so clearing the text clears the answer.
   *
   * @inheritDoc
   */
  @computed get answer(): TextAnswer {
    return { type: 'Text', value: this.value }
  }

  /** @inheritDoc */
  @action.bound
  set(question: TextQuestion) {
    this.value = question.answer?.value ?? ''
    this.editingValue = this.value
  }

  /**
   * Sets the text being typed.
   *
   * @param value
   */
  @action.bound
  setEditingValue(value: string) {
    this.editingValue = value
  }

  /** @inheritDoc */
  @action.bound
  flush() {
    const value = this.editingValue.trim()
    const isDirty = value !== this.value
    this.value = value
    this.editingValue = value
    if (isDirty) {
      this.notifyAnswerChanged()
    }
  }

  /** @inheritDoc */
  validate(): QuestionValidationResult {
    return this.isAnswered
      ? QuestionValidationResult.ok()
      : QuestionValidationResult.failed(['This field is required.'])
  }

  /** @inheritDoc */
  protected formatTextAnswer(): string {
    return this.value
  }
}
